import { callTool } from "../api";
import { emptyCampaign } from "../ratings";
import type { CampaignJourneyData } from "../ratings";
import type { ContactDossier, ContactRecord } from "./types";

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function str(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value).trim();
  return text || null;
}

export async function loadContactDossier(
  contactId: string,
  campaignKey: string,
  viewer?: string,
): Promise<ContactDossier> {
  const contact = await callTool<ContactRecord>("contact_get", {
    contact_id: contactId,
    campaign_key: campaignKey,
  });
  let account_name: string | null = null;
  let segment_primary: string | null = null;
  let booth: string | null = null;
  let hall: string | null = null;
  let campaign: CampaignJourneyData = emptyCampaign(campaignKey);

  if (contact.account_id) {
    try {
      const cls = await callTool<{ classification?: Record<string, unknown> | null }>("classification_get", {
        account_id: contact.account_id,
      });
      segment_primary = str(cls.classification?.segment_primary);
    } catch {
      /* optional */
    }
    try {
      const acct = await callTool<{ name?: string | null }>("account_get", { account_id: contact.account_id });
      account_name = str(acct.name);
    } catch {
      /* optional */
    }
  }

  try {
    const search = await callTool<{ results?: Record<string, unknown>[] }>("contact_search", {
      campaign_key: campaignKey,
      viewer,
      query: contact.email ?? undefined,
      limit: 5,
    });
    const hit = (search.results ?? []).find((row) => str(row.contact_id) === contactId);
    if (hit) {
      account_name = account_name ?? str(hit.account_name);
      booth = str(hit.booth);
      hall = str(hit.hall);
      const journey = hit.campaign;
      if (journey && typeof journey === "object") {
        campaign = journey as CampaignJourneyData;
      }
    }
  } catch {
    /* optional enrichment */
  }

  if (!campaign.filled && !campaign.activities) {
    try {
      const journey = await callTool<{ activities?: CampaignJourneyData["activities"] }>("campaign_journey_get", {
        contact_id: contactId,
        campaign_key: campaignKey,
      });
      const activities = journey.activities ?? {};
      const filled = Object.values(activities).filter((value) => value != null).length;
      campaign = {
        campaign_key: campaignKey,
        activities,
        filled,
        total: 4,
      };
    } catch {
      campaign = emptyCampaign(campaignKey);
    }
  }

  return {
    contact,
    account_name,
    segment_primary,
    campaign,
    booth,
    hall,
  };
}

export function provenanceFromContact(contact: ContactRecord): string[] {
  const lines: string[] = [];
  if (contact.consent_basis) lines.push(`Consent: ${contact.consent_basis}`);
  if (contact.event_context) lines.push(`Context: ${contact.event_context}`);
  if (contact.photo?.source_type) {
    const when = contact.photo.captured_at ? ` · ${contact.photo.captured_at}` : "";
    lines.push(`Photo: ${contact.photo.source_type}${when}`);
  }
  const raw = asRecord(contact.raw_attributes);
  if (raw?.badge_raw) lines.push(`Badge: ${String(raw.badge_raw)}`);
  return lines;
}
